// Centralized helpers: sanitize base URLs and build HTTP/WS endpoints.

export function sanitizeBase(input?: string): string {
  const v = (input ?? '').trim().replace(/\s+/g, '');
  return v.replace(/\/+$/, '');
}

export function getBrowserBase(): string {
  return sanitizeBase(process.env.NEXT_PUBLIC_API_BASE_URL);
}

export function getServerBase(): string {
  // Prefer API_DOMAIN if set (existing production env var)
  if (process.env.API_DOMAIN) {
    return `https://${process.env.API_DOMAIN}`;
  }
  return sanitizeBase(process.env.API_BASE_HTTP || process.env.NEXT_PUBLIC_API_BASE_HTTP || process.env.NEXT_PUBLIC_API_BASE_URL);
}

export function join(base: string, path: string): string {
  const b = sanitizeBase(base);
  const p = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `${b}${p}`;
}

// Browser-friendly join that builds absolute URLs using the public browser base.
// Back-compat: several client modules import this symbol.
export function joinBrowser(path: string): string {
  return join(getBrowserBase(), path);
}

export function toWs(url: string): string {
  try {
    const u = new URL(url);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return u.toString();
  } catch {
    // Fallback: naive replace for already-joined URLs
    return url.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:');
  }
}

// Back-compat: some server components import API_BASE directly
// On the server: use API_DOMAIN / API_BASE_HTTP / NEXT_PUBLIC_API_BASE_URL.
// In the browser: return '' so callers issue same-origin requests
// (the Next.js `/api/:path*` rewrite proxies to API_BASE_HTTP). This avoids
// CORS preflight failures when hitting a backend that doesn't list the
// dev origin in Access-Control-Allow-Origin.
export const API_BASE = typeof window === 'undefined'
  ? (
      process.env.API_DOMAIN ? `https://${process.env.API_DOMAIN}` :
      getServerBase() || 'http://localhost:5151'
    )
  : '';
